import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addCart, removeOrder } from '../../redux/menu/cartSlice';
import { FaInfoCircle } from 'react-icons/fa';
import VegNonVegIcon from '../Common/VegNonVegIcons';
import { isProductAvailable, getNextAvailableTime, formatTimeForDisplay } from '../../utils/timeAvailability';
import useTheme from '../../hooks/useTheme';
import { useTranslation } from 'react-i18next';

const FoodItem = ({ image, name, description, price, id, currency, options, is_veg, variants, productOptions, sizes, ProductVariants, categoryName, categoryDescription, showAddToCart = true, highlightInfoIcon = false, highlight = false, status, ...rest }) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const isArabic = i18n.language === 'ar';
    
    // Use Arabic name if available and current language is Arabic
    const displayName = (isArabic && rest?.name_ar) ? rest.name_ar : name;
    const displayDescription = (isArabic && rest?.description_ar) ? rest.description_ar : description;
    
    // Check if product is available based on time
    const isTimeAvailable = isProductAvailable(rest?.ProductCategory);
    const nextAvailableTime = getNextAvailableTime(rest?.ProductCategory);
    
    // Check if product status is active (status should not be false)
    const isStatusActive = status !== false;
    
    // Product is available only if both time and status are valid
    const isAvailable = isTimeAvailable && isStatusActive;
    
    const dispatch = useDispatch();
    const cartItems = useSelector((state) => state.cart.cart);
    const { profile } = useSelector((state) => state.profile);
    const [variantQuantities, setVariantQuantities] = useState({});

    const [showTooltip, setShowTooltip] = useState(false);
    const [showImageModal, setShowImageModal] = useState(false);
    const isClosingFromPopState = useRef(false);

    // Handle browser back button for image modal
    useEffect(() => {
        if (showImageModal) {
            const modalId = `image-modal-${id}`;
            // Push a new entry to history when modal opens
            window.history.pushState({ modalId }, '');

            const handlePopState = (event) => {
                isClosingFromPopState.current = true;
                setShowImageModal(false);
            };

            window.addEventListener('popstate', handlePopState);

            return () => {
                window.removeEventListener('popstate', handlePopState);
                
                // If the modal is closing manually (not via back button)
                // and we are still on the pushed state, go back once to clear it
                if (!isClosingFromPopState.current && window.history.state?.modalId === modalId) {
                    window.history.back();
                }
                isClosingFromPopState.current = false;
            };
        }
    }, [showImageModal, id]);

    // Helper function to format price - remove .00 if decimal part is 00
    const formatPrice = (price) => {
        const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
        const formattedPrice = numericPrice.toFixed(2);
        return formattedPrice.replace('.00', '');
    };
    
    // Memoize variant options to prevent infinite re-renders
    const finalVariantOptions = useMemo(() => {
        // Check for ProductVariants field first (your API field)
        let variantOptions = ProductVariants || [];
        
        // If no ProductVariants, check other possible field names
        if (!variantOptions || variantOptions.length === 0) {
            variantOptions = options || variants || productOptions || sizes || 
                            (rest?.variants) || (rest?.options) || (rest?.productOptions) || (rest?.sizes) || [];
        }
        
        // If variants is a string, try to parse it as JSON
        if (typeof variantOptions === 'string') {
            try {
                variantOptions = JSON.parse(variantOptions);
            } catch (e) {
                variantOptions = [];
            }
        }
        
        // Check for other common field names as fallback
        if (!variantOptions || variantOptions.length === 0) {
            variantOptions = rest?.product_variants || rest?.productVariants || rest?.item_variants || rest?.itemVariants || 
                            rest?.productOptions || rest?.product_options || rest?.variants || rest?.options || [];
        }
        
        return variantOptions || [];
    }, [ProductVariants, options, variants, productOptions, sizes, rest]);

    const finalHasVariants = useMemo(() => {
        return finalVariantOptions && finalVariantOptions.length > 0;
    }, [finalVariantOptions]);
    
    // Sync local state with cart state
    useEffect(() => {
        const syncQuantities = () => {
            const newQuantities = {};
            
            // For products with variants
            if (finalHasVariants) {
                finalVariantOptions.forEach(variant => {
                    const cartItem = cartItems.find(item => item.id === `${id}-${variant.id}`);
                    if (cartItem) {
                        newQuantities[variant.id] = cartItem.quantity;
                    }
                });
            } else {
                // For regular products without variants
                const cartItem = cartItems.find(item => item.id === id);
                if (cartItem) {
                    newQuantities['base'] = cartItem.quantity;
                }
            }
            
            // Only update state if quantities have actually changed
            setVariantQuantities(prevQuantities => {
                const hasChanged = JSON.stringify(prevQuantities) !== JSON.stringify(newQuantities);
                return hasChanged ? newQuantities : prevQuantities;
            });
        };
        
        syncQuantities();
    }, [cartItems, id, finalHasVariants, finalVariantOptions]);
    
    // Get total quantity across all variants
    const getTotalQuantity = () => {
        return Object.values(variantQuantities).reduce((total, qty) => total + qty, 0);
    };
    
    // Get total price across all variants
    const getTotalPrice = () => {
        let total = 0;
        finalVariantOptions.forEach(variant => {
            const qty = variantQuantities[variant.id] || 0;
            const variantPrice = variant.offerPrice && variant.offerPrice !== null ? parseFloat(variant.offerPrice) : parseFloat(variant.price);
            total += variantPrice * qty;
        });
        return total;
    };

    // Helper function to get the effective price (offer price if available, otherwise regular price)
    const getEffectivePrice = (variant) => {
        return variant.offerPrice && variant.offerPrice !== null ? parseFloat(variant.offerPrice) : parseFloat(variant.price);
    };

    // Helper function to check if offer price is available
    const hasOfferPrice = (variant) => {
        return variant.offerPrice && variant.offerPrice !== null && variant.offerPrice !== "";
    };

    const handleVariantQuantityChange = (variantId, newQuantity) => {
        if (newQuantity >= 0) {
            const updatedQuantities = { ...variantQuantities };
            
            if (newQuantity > 0) {
                updatedQuantities[variantId] = newQuantity;
                setVariantQuantities(updatedQuantities);
                
                // Add to cart
                if (finalHasVariants) {
                    const variant = finalVariantOptions.find(v => v.id === variantId);
                    const effectivePrice = getEffectivePrice(variant);
                    const variantDisplayName = (isArabic && variant.name_ar) ? variant.name_ar : variant.name;
                    const cartItem = {
                        id: `${id}-${variantId}`,
                        productId: id,
                        name: variantDisplayName && variantDisplayName.trim() !== '' ? `${displayName} (${variantDisplayName})` : displayName,
                        price: effectivePrice,
                        originalPrice: parseFloat(variant.price),
                        offerPrice: variant.offerPrice,
                        quantity: newQuantity,
                        image,
                        currency,
                        is_veg,
                        variant: variant,
                        ...(rest?.code != null && rest?.code !== '' && { code: rest.code }),
                        storeId: profile?.id
                    };
                    dispatch(addCart(cartItem));
                } else {
                    // Regular product without variants
                    const productOfferPrice = rest?.offerPrice;
                    const effectivePrice = productOfferPrice && productOfferPrice !== null ? parseFloat(productOfferPrice) : parseFloat(price);
                    const cartItem = {
                        id: id,
                        name: displayName,
                        price: effectivePrice,
                        originalPrice: parseFloat(price),
                        offerPrice: productOfferPrice,
                        quantity: newQuantity,
                        image,
                        currency,
                        is_veg,
                        ...(rest?.code != null && rest?.code !== '' && { code: rest.code }),
                        storeId: profile?.id
                    };
                    dispatch(addCart(cartItem));
                }
            } else if (newQuantity === 0) {
                delete updatedQuantities[variantId];
                setVariantQuantities(updatedQuantities);
                
                // Remove from cart
                const cartId = finalHasVariants ? `${id}-${variantId}` : id;
                dispatch(removeOrder(cartId));
            }
        }
    };

    // Regular Swiggy/Zomato layout
    if (!highlight) {
        const baseQty = variantQuantities['base'] || 0;
        return (
            <>
            <div className={`w-full py-5 md:py-6 px-4 sm:px-5 md:px-6 flex items-start justify-between transition duration-300 relative lg:border lg:rounded-2xl lg:shadow-sm lg:hover:shadow-md lg:hover:scale-[1.01] ${
                isAvailable ? '' : 'opacity-60 cursor-not-allowed'
            }`}
            style={{
                backgroundColor: theme.colors.surfaceColor,
                borderColor: theme.colors.borderColor
            }}>
                {/* Left side: details */}
                <div className="flex-1 pr-4 text-start">
                    {/* Veg/Non-Veg Badge & Labels */}
                    <div className="flex items-center gap-2 mb-1.5">
                        {profile?.enable_veg && is_veg !== null && (
                            <VegNonVegIcon is_veg={is_veg} className="w-4 h-4 flex-shrink-0" />
                        )}
                        {rest?.badge && (
                            <span className="text-[10px] font-extrabold uppercase px-1.5 py-0.5 rounded shadow-sm text-white"
                                  style={{ backgroundColor: is_veg ? theme.colors.badgeVeg : theme.colors.badgeNonVeg }}>
                                {rest.badge}
                            </span>
                        )}
                        {rest?.seasonal && (
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">
                                {t('common.seasonal')}
                            </span>
                        )}
                    </div>

                    {/* Product Name */}
                    <h3 className="font-bold poppins text-sm sm:text-base md:text-lg mb-1 leading-snug"
                        style={{ color: theme.colors.textPrimary }}>
                        {displayName}
                    </h3>

                    {/* Price display (No Variants) */}
                    {!finalHasVariants && (
                        <div className="font-extrabold text-sm sm:text-base poppins mb-2"
                             style={{ color: theme.colors.textPrimary }}>
                            {rest?.offerPrice && rest?.offerPrice !== null ? (
                                <div className="flex items-center gap-2">
                                    <span style={{ color: theme.colors.offerPriceText }}>
                                        {currency} {formatPrice(parseFloat(rest.offerPrice) * (baseQty || 1))}
                                    </span>
                                    <span className="text-gray-400 line-through text-xs font-normal">
                                        {currency} {formatPrice(parseFloat(price) * (baseQty || 1))}
                                    </span>
                                </div>
                            ) : (
                                <span>{currency} {formatPrice(parseFloat(price) * (baseQty || 1))}</span>
                            )}
                        </div>
                    )}

                    {/* Description */}
                    {displayDescription && (
                        <p className="text-xs sm:text-sm line-clamp-2 mb-3 max-w-xl leading-relaxed"
                           style={{ color: theme.colors.textSecondary }}>
                            {displayDescription}
                        </p>
                    )}

                    {/* Variants list (inline under details) */}
                    {finalHasVariants && (
                        <div className="flex flex-col gap-2 mt-3 w-full max-w-md">
                            {finalVariantOptions.map((variant) => {
                                const variantQty = variantQuantities[variant.id] || 0;
                                const variantDisplayName = (isArabic && variant.name_ar) ? variant.name_ar : variant.name;
                                return (
                                    <div key={variant.id} className="flex items-center justify-between rounded-lg p-2 border"
                                         style={{ backgroundColor: theme.colors.backgroundColor, borderColor: theme.colors.borderColor }}>
                                            <div className="flex-1 pr-2">
                                                <div className="text-xs font-bold truncate" style={{ color: theme.colors.textPrimary }}>{variantDisplayName}</div>
                                            <div className="text-xs font-extrabold" style={{ color: theme.colors.variantPriceText }}>
                                                {hasOfferPrice(variant) ? (
                                                    <span className="flex items-center gap-1.5">
                                                        <span style={{ color: theme.colors.offerPriceText }}>{currency} {formatPrice(getEffectivePrice(variant))}</span>
                                                        <span className="text-gray-400 line-through font-normal text-[10px]">{currency} {formatPrice(variant.price)}</span>
                                                    </span>
                                                ) : (
                                                    <span>{currency} {formatPrice(variant.price)}</span>
                                                )}
                                            </div>
                                        </div>
                                        
                                        {showAddToCart && (
                                            variantQty === 0 ? (
                                                <button 
                                                    onClick={() => handleVariantQuantityChange(variant.id, 1)}
                                                    disabled={!isAvailable}
                                                    className="px-3 py-1 text-xs font-bold rounded-md shadow-sm border transition-all hover:scale-105"
                                                    style={isAvailable ? {
                                                        backgroundColor: theme.colors.surfaceColor,
                                                        color: theme.colors.primaryColor,
                                                        borderColor: theme.colors.borderColor,
                                                    } : {}}
                                                >
                                                    ADD
                                                </button>
                                            ) : (
                                                <div className="flex items-center gap-1.5 rounded-md p-1 shadow-sm border"
                                                     style={{ backgroundColor: theme.colors.surfaceColor, borderColor: theme.colors.borderColor }}>
                                                    <button 
                                                        onClick={() => handleVariantQuantityChange(variant.id, variantQty - 1)}
                                                        disabled={!isAvailable}
                                                        className="w-5 h-5 text-gray-500 rounded flex items-center justify-center hover:bg-gray-100 text-xs font-extrabold"
                                                    >
                                                        -
                                                    </button>
                                                    <span className="text-xs font-bold min-w-[12px] text-center" style={{ color: theme.colors.textPrimary }}>{variantQty}</span>
                                                    <button 
                                                        onClick={() => handleVariantQuantityChange(variant.id, variantQty + 1)}
                                                        disabled={!isAvailable}
                                                        className="w-5 h-5 text-gray-500 rounded flex items-center justify-center hover:bg-gray-100 text-xs font-extrabold"
                                                    >
                                                        +
                                                    </button>
                                                </div>
                                            )
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    
                    {/* Total Price Display - Only for products with variants */}
                    {finalHasVariants && getTotalQuantity() > 0 && (
                        <div className="mt-3 text-xs font-medium" style={{ color: theme.colors.textSecondary }}>
                            {t('order_summary.total')}: {getTotalQuantity()} {t('common.items')} &bull; <span className="font-extrabold" style={{ color: theme.colors.textPrimary }}>{currency} {formatPrice(getTotalPrice())}</span>
                        </div>
                    )}
                </div>

                {/* Right side: image & overlapping button */}
                <div className="relative flex flex-col items-center ml-4">
                    {/* Product Image */}
                    <div className="w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow relative">
                        <img 
                            className="w-full h-full object-cover cursor-pointer hover:scale-105 transition duration-300" 
                            src={`${image}`} 
                            alt={displayName}
                            onClick={() => setShowImageModal(true)}
                        />
                        
                        {/* Info tooltip button */}
                        <div className="absolute top-1.5 left-1.5 z-10">
                            <div className="relative">
                                <button
                                    onMouseEnter={() => setShowTooltip(true)}
                                    onMouseLeave={() => setShowTooltip(false)}
                                    onTouchStart={() => setShowTooltip(!showTooltip)}
                                    className="w-5 h-5 rounded-full flex items-center justify-center bg-black/60 border border-white/20 text-white hover:bg-black/80 transition-colors shadow"
                                >
                                    <FaInfoCircle className="w-2.5 h-2.5" />
                                </button>
                                
                                {showTooltip && (
                                    <div className={`absolute top-6 ${isArabic ? 'right-0' : 'left-0'} z-30 bg-black/90 text-white rounded-lg p-2 shadow-xl min-w-[120px] max-w-[180px] text-xs border border-white/10 backdrop-blur-sm`}>
                                        <div className="font-semibold text-blue-300 mb-0.5 truncate">
                                            {(isArabic && rest?.ProductCategory?.name_ar) ? rest.ProductCategory.name_ar : (categoryName || 'Category')}
                                        </div>
                                        {((isArabic && rest?.ProductCategory?.description_ar) || categoryDescription) && (
                                            <div className="opacity-90 leading-tight">
                                                {(isArabic && rest?.ProductCategory?.description_ar) ? rest.ProductCategory.description_ar : categoryDescription}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Availability overlay */}
                        {!isAvailable && (
                            <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center text-center p-1 text-[10px] font-bold text-white z-20">
                                <span>{t('common.currently_unavailable')}</span>
                                {isStatusActive && nextAvailableTime && (
                                    <span className="opacity-95 text-[9px] mt-0.5">
                                        {formatTimeForDisplay(nextAvailableTime)}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>

                    {/* ADD / Quantity selector overlapping the bottom edge of image */}
                    {showAddToCart && !finalHasVariants && (
                        <div className="absolute -bottom-3 z-10">
                            {baseQty === 0 ? (
                                <button 
                                    onClick={() => handleVariantQuantityChange('base', 1)}
                                    disabled={!isAvailable}
                                    className={`w-20 h-8 sm:w-24 sm:h-9 rounded-lg text-xs md:text-sm font-extrabold uppercase shadow-md transition-all active:scale-95 ${
                                        isAvailable 
                                            ? 'hover:bg-gray-55/10' 
                                            : 'text-gray-400 cursor-not-allowed'
                                    }`}
                                    style={isAvailable ? {
                                        backgroundColor: theme.colors.surfaceColor,
                                        color: theme.colors.primaryColor,
                                        borderColor: theme.colors.borderColor,
                                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                                    } : {}}
                                >
                                    ADD +
                                </button>
                            ) : (
                                <div className="w-20 h-8 sm:w-24 sm:h-9 rounded-lg flex items-center justify-between px-2.5 shadow-md border"
                                     style={{ backgroundColor: theme.colors.surfaceColor, borderColor: theme.colors.borderColor }}>
                                    <button 
                                        onClick={() => handleVariantQuantityChange('base', baseQty - 1)}
                                        disabled={!isAvailable}
                                        className="text-gray-500 font-extrabold text-sm sm:text-base w-6 h-full flex items-center justify-center hover:text-red-500 transition-colors"
                                    >
                                        -
                                    </button>
                                    <span className="text-xs sm:text-sm font-extrabold" style={{ color: theme.colors.textPrimary }}>{baseQty}</span>
                                    <button 
                                        onClick={() => handleVariantQuantityChange('base', baseQty + 1)}
                                        disabled={!isAvailable}
                                        className="text-gray-500 font-extrabold text-sm sm:text-base w-6 h-full flex items-center justify-center hover:text-green-600 transition-colors"
                                    >
                                        +
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            
            {/* Image Modal */}
            {showImageModal && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={() => setShowImageModal(false)}>
                    <div className="relative max-w-4xl max-h-full bg-white rounded-lg overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
                        <button 
                            onClick={() => setShowImageModal(false)}
                            className="absolute top-4 right-4 z-10 bg-black bg-opacity-50 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-opacity-75 transition-all duration-200"
                        >
                            ×
                        </button>
                        <img 
                            src={image} 
                            alt={displayName}
                            className="w-full h-auto max-h-[80vh] object-contain"
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
                            <h3 className="text-white text-lg font-semibold">{displayName}</h3>
                        </div>
                    </div>
                </div>
            )}
            </>
        )
    }

    // Highlighted card layout (spans 2 columns / full width on desktop)
    const baseQty = variantQuantities['base'] || 0;
    return (
        <>
        <div className={`col-span-2 w-full p-4 md:p-6 rounded-2xl flex items-start justify-between border shadow-md relative overflow-hidden transition-all duration-300 ${
            isAvailable ? 'hover:shadow-lg' : 'opacity-60 cursor-not-allowed'
        }`}
        style={{
            background: `linear-gradient(to right, ${theme.name === 'cookbook' || theme.name === 'palm' ? 'rgba(255, 106, 0, 0.08)' : 'rgba(251, 191, 36, 0.08)'}, ${theme.colors.surfaceColor}, ${theme.name === 'cookbook' || theme.name === 'palm' ? 'rgba(255, 106, 0, 0.08)' : 'rgba(251, 191, 36, 0.08)'})`,
            borderColor: theme.name === 'cookbook' || theme.name === 'palm' ? theme.colors.borderColor : '#fcd34d'
        }}>
            {/* Left side: details */}
            <div className="flex-1 pr-4 text-start">
                {/* Veg/Non-Veg Badge & Labels */}
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold shadow-sm border border-amber-200 bg-amber-100 text-amber-800 flex items-center gap-1">
                        ⭐ {t('common.featured')}
                    </span>
                    {profile?.enable_veg && is_veg !== null && (
                        <VegNonVegIcon is_veg={is_veg} className="w-4 h-4 flex-shrink-0" />
                    )}
                    {rest?.badge && (
                        <span className="text-[10px] font-extrabold uppercase px-1.5 py-0.5 rounded shadow-sm text-white"
                              style={{ backgroundColor: is_veg ? theme.colors.badgeVeg : theme.colors.badgeNonVeg }}>
                            {rest.badge}
                        </span>
                    )}
                    {rest?.seasonal && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">
                            {t('common.seasonal')}
                        </span>
                    )}
                </div>

                {/* Product Name */}
                <h3 className="font-extrabold poppins text-base sm:text-lg md:text-xl mb-1.5 leading-snug"
                    style={{ color: theme.colors.textPrimary }}>
                    {displayName}
                </h3>

                {/* Price display (No Variants) */}
                {!finalHasVariants && (
                    <div className="font-black text-base sm:text-lg poppins mb-2.5"
                         style={{ color: theme.colors.textPrimary }}>
                        {rest?.offerPrice && rest?.offerPrice !== null ? (
                            <div className="flex items-center gap-2.5">
                                <span style={{ color: theme.colors.offerPriceText }}>
                                    {currency} {formatPrice(parseFloat(rest.offerPrice) * (baseQty || 1))}
                                </span>
                                <span className="text-gray-400 line-through text-xs font-normal">
                                    {currency} {formatPrice(parseFloat(price) * (baseQty || 1))}
                                </span>
                            </div>
                        ) : (
                            <span>{currency} {formatPrice(parseFloat(price) * (baseQty || 1))}</span>
                        )}
                    </div>
                )}

                {/* Description */}
                {displayDescription && (
                    <p className="text-xs sm:text-sm mb-3.5 max-w-xl leading-relaxed"
                       style={{ color: theme.colors.textSecondary }}>
                        {displayDescription}
                    </p>
                )}

                {/* Variants list (inline under details) */}
                {finalHasVariants && (
                    <div className="flex flex-col gap-2 mt-3 w-full max-w-md">
                        {finalVariantOptions.map((variant) => {
                            const variantQty = variantQuantities[variant.id] || 0;
                            const variantDisplayName = (isArabic && variant.name_ar) ? variant.name_ar : variant.name;
                            return (
                                <div key={variant.id} className="flex items-center justify-between rounded-lg p-2.5 border"
                                     style={{ backgroundColor: theme.colors.backgroundColor + '90', borderColor: theme.name === 'cookbook' || theme.name === 'palm' ? theme.colors.borderColor : '#fde68a' }}>
                                    <div className="flex-1 pr-2">
                                        <div className="text-xs font-bold truncate" style={{ color: theme.colors.textPrimary }}>{variantDisplayName}</div>
                                        <div className="text-xs font-extrabold" style={{ color: theme.colors.variantPriceText }}>
                                            {hasOfferPrice(variant) ? (
                                                <span className="flex items-center gap-1.5">
                                                    <span style={{ color: theme.colors.offerPriceText }}>{currency} {formatPrice(getEffectivePrice(variant))}</span>
                                                    <span className="text-gray-400 line-through font-normal text-[10px]">{currency} {formatPrice(variant.price)}</span>
                                                </span>
                                            ) : (
                                                <span>{currency} {formatPrice(variant.price)}</span>
                                            )}
                                        </div>
                                    </div>
                                    
                                    {showAddToCart && (
                                        variantQty === 0 ? (
                                            <button 
                                                onClick={() => handleVariantQuantityChange(variant.id, 1)}
                                                disabled={!isAvailable}
                                                className="px-3 py-1 text-xs font-bold rounded-md shadow-sm border transition-all hover:scale-105"
                                                style={isAvailable ? {
                                                    backgroundColor: theme.colors.surfaceColor,
                                                    color: theme.colors.primaryColor,
                                                    borderColor: theme.name === 'cookbook' || theme.name === 'palm' ? theme.colors.primaryColor : '#fcd34d',
                                                } : {}}
                                            >
                                                ADD
                                            </button>
                                        ) : (
                                            <div className="flex items-center gap-1.5 rounded-md p-1 shadow-sm border"
                                                 style={{ backgroundColor: theme.colors.surfaceColor, borderColor: theme.name === 'cookbook' || theme.name === 'palm' ? theme.colors.borderColor : '#amber-200' }}>
                                                <button 
                                                    onClick={() => handleVariantQuantityChange(variant.id, variantQty - 1)}
                                                    disabled={!isAvailable}
                                                    className="w-5 h-5 text-gray-500 rounded flex items-center justify-center hover:bg-amber-50 text-xs font-extrabold"
                                                >
                                                    -
                                                </button>
                                                <span className="text-xs font-bold min-w-[12px] text-center" style={{ color: theme.colors.textPrimary }}>{variantQty}</span>
                                                <button 
                                                    onClick={() => handleVariantQuantityChange(variant.id, variantQty + 1)}
                                                    disabled={!isAvailable}
                                                    className="w-5 h-5 text-gray-500 rounded flex items-center justify-center hover:bg-amber-50 text-xs font-extrabold"
                                                >
                                                    +
                                                </button>
                                            </div>
                                        )
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
                
                {/* Total Price Display - Only for products with variants */}
                {finalHasVariants && getTotalQuantity() > 0 && (
                    <div className="text-start mb-2 md:mb-3 lg:mb-4">
                        <div className="text-xs sm:text-sm md:text-base mb-1" style={{ color: theme.colors.textSecondary }}>{t('order_summary.total')}: {getTotalQuantity()} {t('common.items')}</div>
                        <div className={`text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold poppins ${currency === 'AED' ? 'currency-aed' : ''}`}
                             style={{ color: theme.colors.primaryColor }}>
                            {currency} {formatPrice(getTotalPrice())}
                        </div>
                    </div>
                )}
            </div>

            {/* Right side: image & overlapping button */}
            <div className="relative flex flex-col items-center ml-4">
                {/* Product Image */}
                <div className="w-28 h-28 sm:w-32 sm:h-32 md:w-36 md:h-36 rounded-2xl overflow-hidden shadow relative">
                    <img 
                        className="w-full h-full object-cover cursor-pointer hover:scale-105 transition duration-300" 
                        src={`${image}`} 
                        alt={displayName}
                        onClick={() => setShowImageModal(true)}
                    />
                    
                    {/* Info tooltip button */}
                    <div className="absolute top-1.5 left-1.5 z-10">
                        <div className="relative">
                            <button
                                onMouseEnter={() => setShowTooltip(true)}
                                onMouseLeave={() => setShowTooltip(false)}
                                onTouchStart={() => setShowTooltip(!showTooltip)}
                                className="w-5.5 h-5.5 rounded-full flex items-center justify-center bg-black/60 border border-white/20 text-white hover:bg-black/80 transition-colors shadow"
                            >
                                <FaInfoCircle className="w-3 h-3" />
                            </button>
                            
                            {showTooltip && (
                                <div className={`absolute top-6 ${isArabic ? 'right-0' : 'left-0'} z-30 bg-black/95 text-white rounded-lg p-2.5 shadow-2xl min-w-[150px] max-w-[200px] text-xs border border-white/10 backdrop-blur-sm`}>
                                    <div className="font-semibold text-blue-300 mb-0.5 truncate">
                                        {(isArabic && rest?.ProductCategory?.name_ar) ? rest.ProductCategory.name_ar : (categoryName || 'Category')}
                                    </div>
                                    {((isArabic && rest?.ProductCategory?.description_ar) || categoryDescription) && (
                                        <div className="opacity-90 leading-tight">
                                            {(isArabic && rest?.ProductCategory?.description_ar) ? rest.ProductCategory.description_ar : categoryDescription}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Availability overlay */}
                    {!isAvailable && (
                        <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center text-center p-1.5 text-xs font-bold text-white z-20">
                            <span>{t('common.currently_unavailable')}</span>
                            {isStatusActive && nextAvailableTime && (
                                <span className="opacity-95 text-[10px] mt-0.5">
                                    {formatTimeForDisplay(nextAvailableTime)}
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {/* ADD / Quantity selector overlapping the bottom edge of image */}
                {showAddToCart && !finalHasVariants && (
                    <div className="absolute -bottom-3.5 z-10">
                        {baseQty === 0 ? (
                            <button 
                                onClick={() => handleVariantQuantityChange('base', 1)}
                                disabled={!isAvailable}
                                className={`w-24 h-9 sm:w-28 sm:h-10 rounded-lg text-xs md:text-sm font-extrabold uppercase shadow-md transition-all active:scale-95 ${
                                    isAvailable 
                                        ? 'hover:bg-gray-55/10' 
                                        : 'text-gray-400 cursor-not-allowed'
                                    }`}
                                style={isAvailable ? {
                                    backgroundColor: theme.colors.surfaceColor,
                                    color: theme.colors.primaryColor,
                                    borderColor: theme.name === 'cookbook' || theme.name === 'palm' ? theme.colors.primaryColor : '#fcd34d',
                                    boxShadow: '0 4px 6px -1px rgba(245, 158, 11, 0.15), 0 2px 4px -1px rgba(245, 158, 11, 0.08)'
                                } : {}}
                            >
                                ADD +
                            </button>
                        ) : (
                             <div className="w-24 h-9 sm:w-28 sm:h-10 rounded-lg flex items-center justify-between px-3 shadow-md border"
                                  style={{ backgroundColor: theme.colors.surfaceColor, borderColor: theme.name === 'cookbook' || theme.name === 'palm' ? theme.colors.primaryColor : '#fcd34d' }}>
                                <button 
                                    onClick={() => handleVariantQuantityChange('base', baseQty - 1)}
                                    disabled={!isAvailable}
                                    className="text-gray-500 font-extrabold text-sm sm:text-base w-7 h-full flex items-center justify-center hover:text-red-500 transition-colors"
                                >
                                    -
                                </button>
                                 <span className="text-xs sm:text-sm font-extrabold" style={{ color: theme.colors.textPrimary }}>{baseQty}</span>
                                <button 
                                    onClick={() => handleVariantQuantityChange('base', baseQty + 1)}
                                    disabled={!isAvailable}
                                    className="text-gray-500 font-extrabold text-sm sm:text-base w-7 h-full flex items-center justify-center hover:text-green-600 transition-colors"
                                >
                                    +
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
        
        {/* Image Modal */}
        {showImageModal && (
            <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={() => setShowImageModal(false)}>
                <div className="relative max-w-4xl max-h-full bg-white rounded-lg overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
                    <button 
                        onClick={() => setShowImageModal(false)}
                        className="absolute top-4 right-4 z-10 bg-black bg-opacity-50 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-opacity-75 transition-all duration-200"
                    >
                        ×
                    </button>
                    <img 
                        src={image} 
                        alt={displayName}
                        className="w-full h-auto max-h-[80vh] object-contain"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
                        <h3 className="text-white text-lg font-semibold">{displayName}</h3>
                    </div>
                </div>
            </div>
        )}
        </>
    )
}

export default FoodItem
